import { HttpContext, HttpHeaders } from '@angular/common/http';
import deepEqual from 'fast-deep-equal';
import { v4 } from 'uuid';
import { BYPASS_REQUEST_SOURCE_INTERCEPTOR } from '../../constants/bypass-request-source-interceptor.constant';
import { RequestSource } from '../../models/request/request-source.enum';

export function equal(a: unknown, b: unknown): boolean {
  return deepEqual(a, b);
}

export function generateId(): string {
  return v4();
}

export function hasNoValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.filter((value) => !hasNoValue(value)).length === 0;
  } else if (typeof value === 'string') {
    return value.trim() === '';
  }
  return value === null || value === undefined;
}

export function hasValue(value: unknown): boolean {
  return !hasNoValue(value);
}

export function toSentenceCase(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

export function toTitleCase(value: string): string {
  const excludedWords = ['and', 'of', 'on', 'upon', 'with'];
  return value
    .split(/\s+/g)
    .map((word: string) => {
      word = word.toLowerCase();
      if (excludedWords.includes(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.substring(1);
    })
    .join(' ');
}

export function removeDuplicates<T>(value: T[]): T[] {
  return value.filter((item, itemIndex: number) => {
    const index = value.findIndex((comparison) => equal(item, comparison));
    return index === itemIndex;
  });
}

export function removeNullValues(value: null): undefined;
export function removeNullValues<T>(value: T): T;
export function removeNullValues<T>(value: T): unknown {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.map((child) => removeNullValues(child));
  }
  if (typeof value === 'object') {
    return Object.entries(value).reduce(
      (filteredValue: T, [key, child]) => ({
        ...filteredValue,
        [key]: removeNullValues(child),
      }),
      value,
    );
  }
  return value;
}

export function removeEmptyValues(value: null): undefined;
export function removeEmptyValues<T extends object>(value: T): Partial<T>;
export function removeEmptyValues<T extends unknown[]>(value: T): T;
export function removeEmptyValues<T>(value: T): T;
export function removeEmptyValues<T>(value: T): unknown {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return undefined;
  }
  if (Array.isArray(value)) {
    const filteredArray = value
      .map((child) => removeEmptyValues(child))
      .filter((childValue) => hasValue(childValue));

    return filteredArray.length ? filteredArray : undefined;
  }
  if (typeof value === 'object') {
    const filteredObject = Object.entries(value).reduce(
      (filteredValue: T, [key, child]) => {
        const childValue = removeEmptyValues(child);
        if (hasNoValue(childValue)) {
          return filteredValue;
        }
        return {
          ...filteredValue,
          [key]: childValue,
        };
      },
      {} as T,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any;

    if (Object.keys(filteredObject).length) {
      return filteredObject;
    }
    return undefined;
  }
  return value;
}

export function buildMultiQuoteHttpOptions(isMultiPlanJourney: boolean): {
  headers?: HttpHeaders;
  context?: HttpContext;
} {
  if (!isMultiPlanJourney) {
    return {};
  }

  const headers = new HttpHeaders({
    'request-source': RequestSource.DomesticAndGeneralUKMultiQuote,
  });

  const context = new HttpContext().set(
    BYPASS_REQUEST_SOURCE_INTERCEPTOR,
    true,
  );

  return { headers, context };
}
